import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { useI18nLocaleSync } from '../hooks/useI18nLocaleSync';
import { useAppLocale } from '../hooks/useAppLocale';
import { pathWithLocale } from '../lib/locale';
import type { GameChoice, GeneratedSet, LottoDb, LottoDbKr, MatchInfo } from '../types/lotto';
import { loadKrDb, loadUsDb } from '../lib/loadDb';
import { drawsFor, findBestMatch, GAMES, isJackpotMatch, simulate } from '../lib/lotto';
import { LottoSetList } from '../components/LottoSetList';
import { MatchInfoPanel } from '../components/MatchInfoPanel';

type SimType = 'us_powerball' | 'us_megamillions';

export function Simulate() {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const lp = useLocalizedPath();
  const krUi = locale === 'ko';

  useI18nLocaleSync();

  usePageMeta({
    title: t('simulate.metaTitle'),
    description: t('simulate.metaDesc'),
    path: pathWithLocale('/simulate', locale),
  });

  const [dbUs, setDbUs] = useState<LottoDb | null>(null);
  const [dbKr, setDbKr] = useState<LottoDbKr | null>(null);
  const [lottoType, setLottoType] = useState<SimType>('us_powerball');
  const [sets, setSets] = useState<GeneratedSet[]>([]);
  const [matches, setMatches] = useState<(MatchInfo | null)[]>([]);

  useEffect(() => {
    loadUsDb().then(setDbUs);
    loadKrDb().then(setDbKr);
  }, []);

  // Korean UI simulates Lotto 6/45; other locales pick a US game.
  const game: GameChoice = krUi ? 'kr_lotto645' : lottoType;
  const rules = GAMES[game];
  const draws = drawsFor(game, dbUs, dbKr);

  const run = () => {
    if (!draws) return;
    const allSets = simulate(draws, rules);
    setSets(allSets);
    setMatches(allSets.map((s) => findBestMatch(draws, s)));
  };

  const dbOk = !!draws;

  return (
    <main className="content">
      <nav className="breadcrumb" aria-label={t('layout.breadcrumbLabel')}>
        <Link to={lp('/')}>{t('breadcrumb.home')}</Link>
        <span className="sep">/</span>
        <span className="current">{krUi ? t('simulate.breadcrumbKr') : t('simulate.breadcrumbUs')}</span>
      </nav>

      <section className="center-block">
        <h1>{krUi ? t('simulate.titleKr') : t('simulate.titleUs')}</h1>
        <p className="subtitle">{krUi ? t('simulate.subtitleKr') : t('simulate.subtitleUs')}</p>

        {!dbOk && (
          <div className="db-status-warning" role="alert">
            {krUi ? t('simulate.dbWarnKr') : t('simulate.dbWarnUs')}
          </div>
        )}

        <div className="feature-banner">
          {!krUi && <div className="feature-banner-title">{t('simulate.bannerTitle')}</div>}
          {krUi ? (
            <p dangerouslySetInnerHTML={{ __html: t('simulate.bannerKr') }} />
          ) : (
            <>
              <p dangerouslySetInnerHTML={{ __html: t('simulate.bannerUs1') }} />
              <p>{t('simulate.bannerUs2')}</p>
            </>
          )}
        </div>

        <div className="lotto-type-selector">
          {!krUi && (
            <select value={lottoType} onChange={(e) => setLottoType(e.target.value as SimType)} aria-label={t('simulate.gameLabel')}>
              <option value="us_powerball">{t('home.gameNamePowerball')}</option>
              <option value="us_megamillions">{t('home.gameNameMega')}</option>
            </select>
          )}
          <button type="button" className="simulate-btn generate-btn-inline" onClick={run} disabled={!dbOk}>
            {krUi ? t('simulate.generateKr') : t('simulate.generate')}
          </button>
        </div>

        {krUi ? (
          <LottoSetList
            sets={sets}
            bonusName={rules.bonusName}
            emptyText={t('home.emptySets')}
            getSetLabel={(i) => t('home.setLabel', { n: i + 1 })}
            renderAfterRow={(i) => {
              const m = matches[i];
              return m && isJackpotMatch(rules, m) ? <MatchInfoPanel match={m} rules={rules} bonusName={rules.bonusName} /> : null;
            }}
          />
        ) : (
          <LottoSetList
            sets={sets}
            bonusName={rules.bonusName}
            renderAfterRow={(i) => {
              const m = matches[i];
              return m && isJackpotMatch(rules, m) ? <MatchInfoPanel match={m} rules={rules} bonusName={rules.bonusName} /> : null;
            }}
          />
        )}
      </section>

      <section className="center-block" style={{ marginTop: 28 }}>
        <article className="about-section">
          <h2 style={{ fontSize: '1.15em', marginTop: 8, marginBottom: 12 }}>
            {t('simulate.eduTitle')}
          </h2>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('simulate.eduIntro')}
          </p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8 }}>
            {t('simulate.eduOddsTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('simulate.eduOddsBody1')}
          </p>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('simulate.eduOddsBody2')}
          </p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8 }}>
            {t('simulate.eduHistoryTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('simulate.eduHistoryBody1')}
          </p>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            {t('simulate.eduHistoryBody2')}
          </p>
          <h3 style={{ fontSize: '1.05em', marginTop: 20, marginBottom: 8 }}>
            {t('simulate.eduDiffTitle')}
          </h3>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            <Trans i18nKey="simulate.eduDiffBody1" components={{ home: <Link to={lp('/')} /> }} />
          </p>
          <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)' }}>
            <Trans i18nKey="simulate.eduDiffBody2" components={{ rng: <Link to="/articles/how-rng-works" />, odds: <Link to="/articles/understanding-lottery-odds" /> }} />
          </p>
        </article>
      </section>
    </main>
  );
}
